import { forwardRef, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { inputStyle } from "@/pages/Login";
import { A } from "@/lib/authStyles";

type PasswordInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "type" | "style"
> & {
  /** Optional override style merged on top of the shared auth input style. */
  style?: React.CSSProperties;
};

/**
 * Auth-styled password input with a built-in eye toggle that lets the user
 * peek at what they're typing. Visibility state is fully internal — it's a
 * UX affordance only, never persisted or surfaced to parents.
 */
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  function PasswordInput({ style, ...inputProps }, ref) {
    const [visible, setVisible] = useState(false);
    return (
      <div style={{ position: "relative" }}>
        <input
          {...inputProps}
          ref={ref}
          type={visible ? "text" : "password"}
          style={{ ...inputStyle, paddingRight: 44, ...style }}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? "Hide password" : "Show password"}
          aria-pressed={visible}
          tabIndex={-1}
          style={{
            position: "absolute",
            right: 6,
            top: "50%",
            transform: "translateY(-50%)",
            background: "transparent",
            border: "none",
            color: A.sub,
            cursor: "pointer",
            padding: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 8,
          }}
        >
          {visible ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    );
  },
);
